import React, { useState, useEffect, useRef } from 'react';
import './StoryPlayer.css';
import { RenderPage } from './components/FlipbookPreview';

const StoryPlayer = ({ album, onClose }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [progress, setProgress] = useState(0);
  const [showEndCard, setShowEndCard] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [showSettings, setShowSettings] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(1.0); // 1.0 = normal, 1.5 = faster, 0.5 = slower
  const [isMuted, setIsMuted] = useState(false);
  const [transitionDirection, setTransitionDirection] = useState('forward'); // 'forward' or 'backward'
  const [wasPlayingBeforeSettings, setWasPlayingBeforeSettings] = useState(false); // Track if was playing before settings
  const [showSpeedIndicator, setShowSpeedIndicator] = useState(false); // YouTube-style speed indicator
  const [showSpeedDropdown, setShowSpeedDropdown] = useState(false); // Dropdown menu state
  
  // Scale factor for album pages so they always fit on screen
  const [pageScale, setPageScale] = useState(1);

  const timerRef = useRef(null);
  const progressIntervalRef = useRef(null);
  const startTimeRef = useRef(null);
  const audioRef = useRef(null);

  const pages = album.pages || [];
  const currentPage = pages[currentIndex];

  const isTimelinePage = !!(
    currentPage &&
    (
      currentPage.type === 'timeline' ||
      currentPage.albumPage?.type === 'timeline' ||
      currentPage.albumPage?.templateKey === 'timeline'
    )
  );

  // Debug logging for timeline pages
  useEffect(() => {
    if (currentPage) {
      const pageType = currentPage.type || currentPage.albumPage?.type || currentPage.albumPage?.templateKey;
      if (pageType === 'timeline' || pageType?.includes('timeline')) {
        console.log('[StoryPlayer] TIMELINE PAGE DEBUG:', {
          index: currentIndex,
          type: pageType,
          hasAlbumPage: !!currentPage.albumPage,
          hasPhotos: currentPage.photos?.length || 0,
          hasItems: currentPage.albumPage?.items?.length || 0,
          albumPageStructure: currentPage.albumPage,
          photoUrls: currentPage.photos,
          templateKey: currentPage.albumPage?.templateKey,
          meta: currentPage.albumPage?.meta
        });
      }
    }
  }, [currentIndex, currentPage]);

  // Calculate duration for current page with speed multiplier
  const calculatePageDuration = (page) => {
    const baseTime = 3000; // 3 seconds base
    const hasText = (page.caption || page.journalText);
    const textBonus = hasText ? 2000 : 0;
    const duration = baseTime + textBonus;
    
    // Apply speed multiplier (faster speed = shorter duration)
    return duration / playbackSpeed;
  };

  const currentDuration = currentPage ? calculatePageDuration(currentPage) : 3000;

  // Background music handling
  useEffect(() => {
    // Create audio element for background music
    if (!audioRef.current) {
      audioRef.current = new Audio();
      audioRef.current.loop = true;
      audioRef.current.volume = 0.3; // 30% volume
      
      // You can set your music URL here or make it configurable
      // For now, using a placeholder - replace with your actual music file
      audioRef.current.src = '/assets/story-music.mp3';
    }

    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  // Control music playback
  useEffect(() => {
    if (!audioRef.current) return;

    if (isPlaying && !isMuted && !showEndCard) {
      audioRef.current.play().catch(err => {
        console.warn('[StoryPlayer] Audio autoplay blocked:', err);
      });
    } else {
      audioRef.current.pause();
    }
  }, [isPlaying, isMuted, showEndCard]);

  // Compute how big we can render a 1024x680 page inside the phone "safe area"
  useEffect(() => {
    const computeScale = () => {
      const vw = window.innerWidth || 0;
      const vh = window.innerHeight || 0;

      // Reserve space for top controls + status bar and bottom nav bar
      const SAFE_TOP = 72;   // progress bar + speed/music + status bar
      const SAFE_BOTTOM = 40; // Android nav bar
      const safeHeight = Math.max(0, vh - SAFE_TOP - SAFE_BOTTOM);
      const safeWidth = Math.max(0, vw - 16); // 8px left + 8px right padding

      const baseWidth = 1024;
      const baseHeight = 680;

      const scale = Math.min(safeWidth / baseWidth, safeHeight / baseHeight) || 1;
      setPageScale(scale > 0 ? scale : 1);
    };

    computeScale();
    window.addEventListener('resize', computeScale);
    window.addEventListener('orientationchange', computeScale);

    return () => {
      window.removeEventListener('resize', computeScale);
      window.removeEventListener('orientationchange', computeScale);
    };
  }, []);

  // YouTube-style: No auto-pause when changing settings
  // Settings can be changed while story continues playing

  // Handle speed change WITHOUT restarting the page
  const handleSpeedChange = (newSpeed) => {
    // Calculate current progress percentage
    const currentProgressMs = elapsedTime;
    
    // Update speed
    setPlaybackSpeed(newSpeed);
    
    // Keep the same elapsed TIME (not percentage)
    // The timer will continue from this point with new speed
    
    // Show speed indicator briefly
    setShowSpeedIndicator(true);
    setTimeout(() => setShowSpeedIndicator(false), 1500);
    
    // Close dropdown
    setShowSpeedDropdown(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    if (showSpeedDropdown) {
      const closeDropdown = () => setShowSpeedDropdown(false);
      document.addEventListener('click', closeDropdown);
      return () => document.removeEventListener('click', closeDropdown);
    }
  }, [showSpeedDropdown]);

  // Initialize timing when component mounts or when page changes
  useEffect(() => {
    startTimeRef.current = Date.now();
    setElapsedTime(0);
    setProgress(0);
  }, [currentIndex]);

  // Auto-advance to next page
  useEffect(() => {
    if (!isPlaying || showEndCard) {
      // Pause: capture elapsed time for resume
      if (!isPlaying && startTimeRef.current) {
        const elapsed = Date.now() - startTimeRef.current;
        setElapsedTime(elapsed);
      }
      return;
    }

    // Calculate duration with current speed
    const calculateDuration = (page) => {
      const baseTime = 3000;
      const hasText = (page.caption || page.journalText);
      const textBonus = hasText ? 2000 : 0;
      return (baseTime + textBonus) / playbackSpeed;
    };
    
    const pageDuration = currentPage ? calculateDuration(currentPage) : 3000;
    const remainingTime = Math.max(0, pageDuration - elapsedTime);
    
    // Set start time for this play session
    startTimeRef.current = Date.now();
    
    // Set timer to advance to next page
    timerRef.current = setTimeout(() => {
      if (currentIndex < pages.length - 1) {
        setTransitionDirection('forward');
        setCurrentIndex(prev => prev + 1);
        // Note: timing reset happens in the initialization effect above
      } else {
        setShowEndCard(true);
        setIsPlaying(false);
      }
    }, remainingTime);

    // Update progress bar smoothly
    progressIntervalRef.current = setInterval(() => {
      const elapsed = Date.now() - startTimeRef.current + elapsedTime;
      const newProgress = Math.min((elapsed / pageDuration) * 100, 100);
      setProgress(newProgress);
    }, 50);

    return () => {
      clearTimeout(timerRef.current);
      clearInterval(progressIntervalRef.current);
    };
  }, [isPlaying, showEndCard, elapsedTime, playbackSpeed, currentIndex, currentPage, pages.length]);

  // Toggle play/pause (center tap)
  const handleTogglePlay = () => {
    setIsPlaying(!isPlaying);
  };

  // Navigate to specific page
  const goToPage = (index) => {
    if (index === currentIndex) return;
    setTransitionDirection(index > currentIndex ? 'forward' : 'backward');
    setCurrentIndex(index);
    // Note: progress/elapsedTime reset handled by initialization effect
    setShowEndCard(false);
    if (!isPlaying) {
      setIsPlaying(true);
    }
  };

  // Next page (right tap)
  const goToNext = () => {
    if (currentIndex < pages.length - 1) {
      setTransitionDirection('forward');
      setCurrentIndex(prev => prev + 1);
      // Note: progress/elapsedTime reset handled by initialization effect
      if (!isPlaying) setIsPlaying(true);
    } else {
      setShowEndCard(true);
      setIsPlaying(false);
    }
  };

  // Previous page (left tap)
  const goToPrevious = () => {
    if (currentIndex > 0) {
      setTransitionDirection('backward');
      setCurrentIndex(prev => prev - 1);
      // Note: progress/elapsedTime reset handled by initialization effect
      if (!isPlaying) setIsPlaying(true);
    }
  };

  // Handle tap zones
  const handleTap = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const width = rect.width;
    
    // Left 25% = previous, Right 25% = next, Center 50% = play/pause
    if (x < width * 0.25) {
      goToPrevious();
    } else if (x > width * 0.75) {
      goToNext();
    } else {
      handleTogglePlay();
    }
  };

  // Restart story
  const handleRestart = () => {
    setCurrentIndex(0);
    // Note: progress/elapsedTime reset handled by initialization effect
    setShowEndCard(false);
    setIsPlaying(true);
    setTransitionDirection('forward');
  };

  // Format duration
  const formatDuration = (ms) => {
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  // Calculate total story duration (accounting for speed)
  const totalDuration = pages.reduce((sum, page) => sum + calculatePageDuration(page), 0);

  // Settings panel
  const SettingsPanel = () => (
    <div className="story-settings-panel" onClick={(e) => e.stopPropagation()}>
      <div className="settings-header">
        <h3>⚙️ Settings</h3>
        <button onClick={() => setShowSettings(false)} className="settings-close">✕</button>
      </div>
      
      <div className="settings-content">
        <div className="setting-item">
          <label>Playback Speed</label>
          <div className="speed-buttons">
            <button 
              className={playbackSpeed === 0.5 ? 'active' : ''}
              onClick={() => setPlaybackSpeed(0.5)}
            >
              0.5x
            </button>
            <button 
              className={playbackSpeed === 1.0 ? 'active' : ''}
              onClick={() => setPlaybackSpeed(1.0)}
            >
              1x
            </button>
            <button 
              className={playbackSpeed === 1.5 ? 'active' : ''}
              onClick={() => setPlaybackSpeed(1.5)}
            >
              1.5x
            </button>
            <button 
              className={playbackSpeed === 2.0 ? 'active' : ''}
              onClick={() => setPlaybackSpeed(2.0)}
            >
              2x
            </button>
          </div>
        </div>

        <div className="setting-item">
          <label>Background Music</label>
          <button 
            className={`music-toggle ${isMuted ? '' : 'active'}`}
            onClick={() => setIsMuted(!isMuted)}
          >
            {isMuted ? '🔇 Muted' : '🔊 Playing'}
          </button>
        </div>

        <div className="settings-info">
          <p>💡 Tap left to go back</p>
          <p>💡 Tap right to go forward</p>
          <p>💡 Tap center to pause/play</p>
        </div>
      </div>
    </div>
  );

  if (showEndCard) {
    return (
      <div className="story-player">
        <div className="story-end-card">
          <div className="end-card-content">
            <h2>✨ {album.title || 'Travel Story'}</h2>
            <p className="end-card-subtitle">
              {pages.length} moments • {formatDuration(totalDuration)}
            </p>
            <div className="end-card-branding">
              <span className="brand-icon">🗺️</span>
              <span className="brand-text">Made with VivaJour</span>
            </div>
            <button className="end-card-button replay" onClick={handleRestart}>
              ↺ Watch Again
            </button>
            {onClose && (
              <button className="end-card-button secondary" onClick={onClose}>
                📖 View Album
              </button>
            )}
            <a 
              href="https://Vivajour.com" 
              className="end-card-button primary"
              target="_blank"
              rel="noopener noreferrer"
            >
              Create Your Own Story
            </a>
          </div>
        </div>
      </div>
    );
  }

  if (!currentPage) {
    return (
      <div className="story-player">
        <div className="story-loading">
          <div className="loading-spinner"></div>
          <p>Loading story...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="story-player" onClick={handleTap}>
      {/* Speed Dropdown Button */}
      <div className="story-speed-container">
        <button 
          className="story-speed-button"
          onClick={(e) => {
            e.stopPropagation();
            setShowSpeedDropdown(!showSpeedDropdown);
          }}
          title="Change playback speed"
        >
          {playbackSpeed}x
        </button>
        
        {/* Dropdown Menu */}
        {showSpeedDropdown && (
          <div className="story-speed-dropdown">
            {[0.5, 0.75, 1.0, 1.25, 1.5, 2.0].map(speed => (
              <button
                key={speed}
                className={`speed-option ${playbackSpeed === speed ? 'active' : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                  handleSpeedChange(speed);
                }}
              >
                {speed === 1.0 ? 'Normal' : `${speed}x`}
                {playbackSpeed === speed && <span className="check-mark">✓</span>}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Music toggle button */}
      {/*
  TEMP DISABLED (Dec 2025)
  Music toggle UI is hidden until UX is finalized.
  To restore: uncomment this block.
  
<button 
  className="story-music-button"
  onClick={(e) => {
    e.stopPropagation();
    setIsMuted(!isMuted);
  }}
>
  {isMuted ? '🔇' : '🔊'}
</button>

*/}


      {/* Speed indicator (shows briefly when changed) */}
      {showSpeedIndicator && (
        <div className="story-speed-indicator">
          <div className="speed-indicator-content">
            <div className="speed-icon">⚡</div>
            <div className="speed-text">{playbackSpeed}x</div>
          </div>
        </div>
      )}

      {/* Progress bars at top */}
      <div className="story-progress-container">
        {pages.map((_, index) => (
          <div 
            key={index} 
            className="story-progress-bar"
            onClick={(e) => {
              e.stopPropagation();
              goToPage(index);
            }}
          >
            <div 
              className="story-progress-fill"
              style={{
                width: index < currentIndex ? '100%' : 
                       index === currentIndex ? `${progress}%` : '0%'
              }}
            />
          </div>
        ))}
      </div>

      {/* Main content with transitions */}
            {/* Main content with transitions */}
      <div className={`story-content story-transition-${transitionDirection}`}>
        {currentPage.albumPage ? (
          // Album-page mode: render the actual album page (including timeline!)
          <div
            className={`story-album-page-wrapper story-page-standard-size ${
              isTimelinePage ? 'story-timeline-page' : '' // <-- Keep the class for the CSS fix below
            }`}
          >
            {/* The old block for the timeline page is removed. */}
            {/* The inner scaling wrapper is now used for ALL album pages, including timeline */}
            <div
              className="story-album-page-inner"
              style={{
                transform: `scale(${pageScale})`,
                transformOrigin: 'center center', // Use center center for all
              }}
            >
              <RenderPage
                page={currentPage.albumPage}
                photoIndex={currentPage.albumPagePhotoIndex || {}}
                isPrinting={false}
              />
            </div>
          </div>
        ) : currentPage.photos && currentPage.photos.length > 0 ? (

          // Photo-based mode with photos
          <>
            {currentPage.photos.length > 1 ? (
              <div className="story-image-grid story-page-standard-size">
                {currentPage.photos.slice(0, 3).map((photoUrl, idx) => (
                  <div
                    key={idx}
                    className={`story-image story-image-grid-item ${isPlaying ? 'ken-burns' : ''}`}
                    style={{
                      backgroundImage: `url(${photoUrl})`
                    }}
                  />
                ))}
              </div>
            ) : (
              <div 
                className={`story-image story-page-standard-size ${isPlaying ? 'ken-burns' : ''}`}
                style={{
                  backgroundImage: `url(${currentPage.photos[0]})`
                }}
              />
            )}

            <div className="story-overlay" />

            <div className="story-text-content">
              <div className="story-header">
                <h3 className="story-title">{album.title || 'Travel Story'}</h3>
                <p className="story-location">{currentPage.location || currentPage.title}</p>
              </div>

              <div className="story-footer">
                {currentPage.caption && (
                  <p className="story-caption">{currentPage.caption}</p>
                )}
                {currentPage.journalText && (
                  <div className="story-journal">
                    <p>{currentPage.journalText}</p>
                  </div>
                )}
              </div>
            </div>
          </>
        ) : (
          // FALLBACK: No photos available (timeline or other special pages)
          <div className="story-placeholder-page">
            <div className="story-overlay" />
            
            <div className="story-placeholder-content">
              <div className="placeholder-icon">
                {currentPage.type === 'timeline' ? '📅' : 
                 currentPage.type === 'map' ? '🗺️' :
                 currentPage.type === 'stats' ? '📊' : '📄'}
              </div>
              
              <h2 className="placeholder-title">
                {currentPage.title || currentPage.location || 'Timeline'}
              </h2>
              
              {currentPage.caption && (
                <div 
                  className="placeholder-caption"
                  dangerouslySetInnerHTML={{ __html: currentPage.caption }}
                />
              )}
              
              {currentPage.journalText && (
                <div className="placeholder-journal">
                  <p>{currentPage.journalText}</p>
                </div>
              )}
              
              <p className="placeholder-hint">
                {currentPage.type === 'timeline' 
                  ? '📱 View in flipbook mode for full timeline'
                  : '📖 View in flipbook mode for full page'}
              </p>
            </div>
          </div>
        )}

        {/* Play/Pause indicator */}
        {!isPlaying && (
          <div className="story-pause-indicator">
            <div className="pause-icon">▶️</div>
            <p>Tap center to resume</p>
          </div>
        )}

        {/* Tap zone indicators (subtle) */}
        <div className="story-tap-zones">
          <div className="tap-zone tap-zone-left" title="Previous">
            <span className="tap-hint">‹</span>
          </div>
          <div className="tap-zone tap-zone-center" title="Play/Pause">
            <span className="tap-hint">{isPlaying ? '❚❚' : '▶'}</span>
          </div>
          <div className="tap-zone tap-zone-right" title="Next">
            <span className="tap-hint">›</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StoryPlayer;